const { mainConfigService, samsungPayConfigService } = require('./services/config');
const { WEB_SDK_CHANNEL, SAMSUNG_PAY_CONFIG } = require('./constants');
const { sdkFetch } = require('./http');
const { generateSamsungOrderNo } = require('./util');
const { SDK_FOPS, SAMSUNG_PAY_ACTION } = require('./variables');

let samsungPayClient = null;
let callbackService = undefined;
let showLoader = undefined;
let hideLoader = undefined;
let validatePayment;
let oneLastCheckGate;

function initializeSamsungPayServices(cbService, callbacks = {}, _validatePayment, _oneLastCheckGate) {
  callbackService = cbService;
  showLoader = callbacks.showLoader;
  hideLoader = callbacks.hideLoader;
  validatePayment = _validatePayment;
  oneLastCheckGate = _oneLastCheckGate;
}

const _getSamsungClient = () => {
  if (samsungPayClient === null) {
    try {
      samsungPayClient = new SamsungPay.PaymentClient({
        environment: samsungPayConfigService.get('environment') || SAMSUNG_PAY_CONFIG.DEFAULT_ENVIRONMENT,
      });
    } catch (error) {
      console.log('Error: Unable to create Samsung Payment client', error);
      return false;
    }
  }
  return samsungPayClient;
};

const loadSamsungPay = async () => {
  const client = _getSamsungClient();
  if (client) {
    const paymentMethods = _getPaymentMethods();
    try {
      const response = await client.isReadyToPay(paymentMethods);
      if (response.result) {
        return await _createSamsungButton();
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  }
  return false;
};

const _getPaymentMethods = () => {
  let paymentMethods = {};
  paymentMethods.version = SAMSUNG_PAY_CONFIG.VERSION;
  paymentMethods.serviceId = samsungPayConfigService.get('service_id');
  paymentMethods.protocol = SAMSUNG_PAY_CONFIG.PROTOCOL;
  paymentMethods.allowedBrands = SAMSUNG_PAY_CONFIG.ALLOWED_BRANDS;
  return paymentMethods;
};

const _getTransactionInfo = () => {
  let transactionInfo = {};
  transactionInfo.orderNumber = generateSamsungOrderNo(mainConfigService.get('session_id'));
  transactionInfo.merchant = {
    name: samsungPayConfigService.get('merchant_name'),
    url: mainConfigService.get('merchant_id'),
  };
  transactionInfo.amount = {
    option: SAMSUNG_PAY_CONFIG.AMOUNT_OPTION,
    currency: samsungPayConfigService.get('currency_code'),
    total: samsungPayConfigService.get('total_price'),
  };

  return transactionInfo;
};

const _createSamsungButton = () => {
  const client = _getSamsungClient();
  return client.createButton({
    onClick: _onSamsungPayButtonClick,
    type: 'buy',
  });
};

const _createPaymentResult = (status) => {
  return {
    status,
    provider: 'MPGS',
  };
};

const _onSamsungPayButtonClick = () => {
  const client = _getSamsungClient();
  const paymentMethods = _getPaymentMethods();
  const transactionInfo = _getTransactionInfo();

  const matchedMethod = mainConfigService.get('payment_methods')?.find(
    (pm) => pm.code === samsungPayConfigService.get('pg_code')
  );
  const trandata = matchedMethod?.trandata;

  if (!trandata) {
    console.warn('Unable to match the samsung service to payment method');
    return;
  }

  validatePayment().then(async (isValid) => {
    if (isValid === false) {
      return
    }

    if (oneLastCheckGate) {
      const proceed = await oneLastCheckGate();
      if (!proceed) return;
    }

    client
    .loadPaymentSheet(paymentMethods, transactionInfo)
      .then(async (paymentCredential) => {
        if (showLoader) showLoader();

        try {
          const response = await processPayment(
            paymentCredential['3DS'].data,
            trandata
          );

          let paymentResult;
          if (response.status === 'success') {
            paymentResult = _createPaymentResult('CHARGED');
            callbackService.callBackSuccess(response);
          } else if (response.status === 'canceled') {
            paymentResult = _createPaymentResult('CANCELED');
            callbackService.callBackCancel(response);
          } else {
            paymentResult = _createPaymentResult('ERRED');
            callbackService.callBackError(response);
          }

          // Hide loader after payment processing completes
          if (hideLoader) hideLoader();

          client.notify(paymentResult);
        } catch (error) {
          // Hide loader on error
          if (hideLoader) hideLoader();

          const paymentResult = _createPaymentResult('ERRED');
          client.notify(paymentResult);
          callbackService.callBackError({
            status: 'error',
            form_of_payment: SDK_FOPS.SAMSUNG_PAY,
            message: error.message || 'Failed to process payment',
          });
        }
      })
      .catch((error) => {
        let paymentResult = _createPaymentResult('ERRED');
        let message = 'Failed to process payment';
        if (error === SAMSUNG_PAY_ACTION.cancel) {
          message = 'User closed the Payment Request UI.';
          paymentResult = _createPaymentResult('CANCELED');
        } else {
          callbackService.callBackError({
            status: 'error',
            form_of_payment: SDK_FOPS.SAMSUNG_PAY,
            message: error.message || message,
          });
        }

        client.notify(paymentResult);
      });

    window.SamsungPay = null;
  })
};

function processPayment(token, trandata) {
  return new Promise((resolve, reject) => {
    const body = {
      trandata,
      enc: token,
    };

    sdkFetch(samsungPayConfigService.get('payment_url'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Merchant-Id': mainConfigService.get('merchant_id'),
        Channel: WEB_SDK_CHANNEL,
        Authorization: `Api-Key ${mainConfigService.get('apiKey')}`,
      },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (response.ok || response.approved) {
          return await response.json();
        } else {
          throw new Error(JSON.stringify(await response.json()));
        }
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(JSON.parse(err.message));
      });
  });
}

exports.initializeSamsungPayServices = initializeSamsungPayServices;
exports.loadSamsungPay = loadSamsungPay;
